import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Building2, Shield, Users, Newspaper, Calendar, LineChart, 
  Wrench, Mail, Twitter, Linkedin, Github 
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { languageCodes, defaultLanguage } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';

export default function Footer() {
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation();
  
  // Get localized path helper
  const getLocalizedPath = (path) => {
    if (i18n.language === defaultLanguage) {
      return path;
    }
    return `/${i18n.language}${path === '/' ? '' : path}`;
  };

  const footerLinks = {
    [t('footer.resources', 'Platform')]: [
      { name: t('brokers.title', 'Broker Directory'), path: '/brokers', icon: Building2 },
      { name: t('charts.title', 'Live Charts'), path: '/charts', icon: LineChart },
      { name: t('calendar.title', 'Economic Calendar'), path: '/calendar', icon: Calendar },
      { name: t('news.title', 'Market News'), path: '/news', icon: Newspaper },
      { name: t('toolbox.title', 'Trading Tools'), path: '/toolbox', icon: Wrench },
      { name: t('traders.title', 'Top Traders'), path: '/traders', icon: Users },
    ],
    [t('footer.support', 'Services')]: [
      { name: t('setup_broker.title', 'Setup Your Own FX Broker'), path: '/setup-broker', icon: Building2, highlight: true },
      { name: t('tournament.title', 'Trading Tournament'), path: '/tournament', hot: true },
      { name: 'Broker Listing', path: '/register' },
      { name: 'Advertise With Us', path: '/contact' },
    ],
    [t('footer.company', 'Company')]: [
      { name: t('footer.about', 'About Us'), path: '/about' },
      { name: t('footer.contact', 'Contact'), path: '/contact' },
      { name: t('footer.careers', 'Careers'), path: '/careers' },
      { name: t('footer.press', 'Press'), path: '/press' },
    ],
    [t('footer.legal', 'Legal')]: [
      { name: t('footer.privacy', 'Privacy Policy'), path: '/legal/privacy' },
      { name: t('footer.terms', 'Terms of Service'), path: '/legal/terms' },
      { name: t('footer.disclaimer', 'Risk Disclosure'), path: '/legal/risk' },
      { name: t('footer.cookies', 'Cookie Policy'), path: '/legal/cookies' },
    ],
  };

  const socialLinks = [
    { name: 'Twitter', icon: Twitter, url: '#' },
    { name: 'LinkedIn', icon: Linkedin, url: '#' },
    { name: 'GitHub', icon: Github, url: '#' },
  ];
  
  return (
    <footer data-testid="footer" className={cn(
      "border-t",
      isDark ? "bg-[#0a0a0f] border-gray-800" : "bg-gray-100 border-gray-200"
    )}>
      {/* Main Footer */}
      <div className="container-app py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to={getLocalizedPath("/")} className="flex items-center gap-2 mb-4">
              <img 
                src="/assets/astra-logo-full.png"
                alt="AstraTerminal" 
                className="h-10 w-auto"
              />
            </Link>
            <p className={cn("text-sm mb-5 leading-relaxed", isDark ? "text-gray-500" : "text-gray-600")}>
              {t('about.subtitle', 'The global forex broker comparison platform trusted by millions of traders worldwide.')}
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  className={cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center transition-all",
                    isDark 
                      ? "bg-gray-800/50 hover:bg-purple-500/20 border border-gray-700/50 hover:border-purple-500/30 text-gray-500 hover:text-purple-400"
                      : "bg-white hover:bg-purple-50 border border-gray-200 hover:border-purple-300 text-gray-500 hover:text-purple-500"
                  )}
                  aria-label={social.name}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            
            {/* Language Switcher - More Visible */}
            <div className="mt-4">
              <LanguageSwitcher variant="prominent" />
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className={cn("font-semibold mb-4 text-sm", isDark ? "text-white" : "text-gray-900")}>{category}</h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          "text-sm transition-colors flex items-center gap-2",
                          isDark ? 'text-gray-500 hover:text-purple-400' : 'text-gray-600 hover:text-purple-500'
                        )}
                      >
                        {link.icon && <link.icon className="h-3.5 w-3.5" />}
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={getLocalizedPath(link.path)}
                        data-testid={link.highlight ? 'setup-broker-footer-link' : undefined}
                        className={cn(
                          "text-sm transition-colors flex items-center gap-2",
                          link.highlight 
                            ? 'text-orange-400 hover:text-orange-300 font-medium' 
                            : link.hot
                            ? 'text-red-400 hover:text-red-300 font-medium'
                            : isDark ? 'text-gray-500 hover:text-purple-400' : 'text-gray-600 hover:text-purple-500'
                        )}
                      >
                        {link.icon && <link.icon className="h-3.5 w-3.5" />}
                        {link.name}
                        {link.hot && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded animate-pulse">
                            HOT
                          </span>
                        )}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className={cn("mt-10 pt-8 border-t", isDark ? "border-gray-800/50" : "border-gray-200")}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>Stay Updated</p>
                <p className="text-xs text-gray-500">Get the latest broker reviews & market insights</p>
              </div>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <input 
                type="email"
                placeholder="Enter your email"
                className={cn(
                  "flex-1 md:w-64 px-4 py-2.5 rounded-lg text-sm focus:outline-none",
                  isDark 
                    ? "bg-gray-800/50 border border-gray-700/50 text-white placeholder:text-gray-500 focus:border-purple-500/50"
                    : "bg-white border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-400"
                )}
              />
              <button className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-sm font-medium rounded-lg transition-all">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={cn("border-t", isDark ? "border-gray-800/50" : "border-gray-200")}>
        <div className="container-app py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Shield className="h-4 w-4 text-green-500" />
              <p className={cn("text-sm", isDark ? "text-gray-500" : "text-gray-600")}>
                {t('footer.copyright', '© 2026 AstraTerminal. All rights reserved.')}
              </p>
            </div>
            <p className={cn("text-xs max-w-2xl text-center md:text-right leading-relaxed", isDark ? "text-gray-600" : "text-gray-500")}>
              <span className="text-yellow-500/80">⚠️ {t('footer.disclaimer', 'Risk Warning')}:</span> Trading forex and CFDs carries significant risk of loss. 
              Past performance is not indicative of future results. Only trade with capital you can afford to lose.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
